import { useEffect, useCallback, useState } from 'react';
import { X, ChevronLeft, ChevronRight, ImageOff } from 'lucide-react';

import type { PhotoRecord } from '@/types/photo';
import { usePhotoSrc } from '@/hooks/use-photo-src';

export interface PhotoLightboxProps {
  photos: PhotoRecord[];
  initialIndex: number;
  onClose: () => void;
  /**
   * Optional URL resolver. If omitted, usePhotoSrc is used to download on demand (M81).
   * Kept for backward compatibility with callers that pre-resolve URLs.
   */
  getUrl?: (photoId: string) => string;
  /** When provided, shown as context label for sit-tagged photos */
  sitContext?: string;
}

/**
 * Single-photo image panel within the lightbox.
 * Uses usePhotoSrc when no external getUrl is provided (M81 hydration path).
 */
function LightboxImage({
  photo,
  getUrl,
}: {
  photo: PhotoRecord;
  getUrl?: (photoId: string) => string;
}) {
  // Pass undefined to usePhotoSrc when getUrl is provided (bypass download path)
  const { src: hookSrc, state, retry } = usePhotoSrc(getUrl ? undefined : photo.id);

  const resolvedSrc = getUrl ? getUrl(photo.id) : hookSrc;

  if (!getUrl && state === 'loading') {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="w-24 h-24 bg-bark-700 animate-pulse rounded-md" />
      </div>
    );
  }

  if (!getUrl && state === 'error') {
    return (
      <button
        type="button"
        onClick={retry}
        className="flex flex-col items-center justify-center gap-2 text-bark-400 hover:text-bark-300 transition-colors"
        aria-label="Photo unavailable - tap to retry"
      >
        <ImageOff size={32} aria-hidden />
        <span className="text-body-sm">Photo unavailable</span>
      </button>
    );
  }

  return (
    <img
      src={resolvedSrc ?? ''}
      alt={photo.originalFilename ?? 'Photo'}
      className="max-w-full max-h-full object-contain select-none"
      data-testid="lightbox-image"
    />
  );
}

/**
 * Full-screen photo lightbox with keyboard + swipe navigation.
 * Tap the backdrop or the X button to close.
 */
export function PhotoLightbox({ photos, initialIndex, onClose, getUrl, sitContext }: PhotoLightboxProps) {
  const [index, setIndex] = useState(initialIndex);

  const hasPrev = index > 0;
  const hasNext = index < photos.length - 1;
  const current = photos[index];

  const goNext = useCallback(() => {
    if (hasNext) setIndex((i) => i + 1);
  }, [hasNext]);

  const goPrev = useCallback(() => {
    if (hasPrev) setIndex((i) => i - 1);
  }, [hasPrev]);

  // Keyboard navigation
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [goNext, goPrev, onClose]);

  // Touch swipe detection
  useEffect(() => {
    let startX = 0;
    function onTouchStart(e: TouchEvent) {
      startX = e.touches[0]?.clientX ?? 0;
    }
    function onTouchEnd(e: TouchEvent) {
      const endX = e.changedTouches[0]?.clientX ?? 0;
      const delta = endX - startX;
      if (Math.abs(delta) > 50) {
        if (delta < 0) goNext();
        else goPrev();
      }
    }
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, [goNext, goPrev]);

  if (!current) return null;

  return (
    <div
      className="ph-no-capture fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      data-testid="photo-lightbox"
      onClick={onClose}
    >
      {/* Photo counter */}
      <div
        className="absolute top-4 left-1/2 -translate-x-1/2 text-white/80 text-body-sm select-none"
        onClick={(e) => e.stopPropagation()}
      >
        {index + 1} of {photos.length}
      </div>

      {/* Sit context label -- shown when the current photo has a sit context */}
      {sitContext && (
        <div
          className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-body-xs select-none bg-black/50 px-3 py-1 rounded-full"
          onClick={(e) => e.stopPropagation()}
          data-testid="lightbox-sit-context"
        >
          {sitContext}
        </div>
      )}

      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
        aria-label="Close"
        data-testid="lightbox-close"
      >
        <X size={18} />
      </button>

      {/* Prev button */}
      {hasPrev && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          aria-label="Previous photo"
          data-testid="lightbox-prev"
        >
          <ChevronLeft size={22} />
        </button>
      )}

      {/* Next button */}
      {hasNext && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          aria-label="Next photo"
          data-testid="lightbox-next"
        >
          <ChevronRight size={22} />
        </button>
      )}

      {/* Main image -- uses usePhotoSrc internally when getUrl is not provided */}
      <div onClick={(e) => e.stopPropagation()}>
        <LightboxImage photo={current} getUrl={getUrl} />
      </div>
    </div>
  );
}
